package org.teddapp.project.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.coroutines.CoroutineScope

/**
 * Les modèles d'écran, gardés d'une rubrique à l'autre.
 *
 * Sans lui, chaque écran recrée son modèle en entrant et relit toute sa part de
 * base : revenir aux Compétences coûtait vingt-neuf requêtes et une recomposition
 * complète. Posé à la racine de l'application, il garde le modèle et ses listes
 * chaudes ([HOT_MS]) : l'écran reprend là où il en était.
 *
 * Le [scope] vient de la racine : il porte l'horloge d'image de la composition, dont
 * certains modèles ont besoin (`withFrameNanos`), et survit aux écrans.
 */
class ScreenModels(val scope: CoroutineScope) {
    private val models = HashMap<Any, Any>()

    @Suppress("UNCHECKED_CAST")
    fun <T : Any> getOrPut(key: Any, create: (CoroutineScope) -> T): T =
        models.getOrPut(key) { create(scope) } as T

    companion object {
        /** Combien de temps une liste observée reste chaude après le départ du dernier écran. */
        const val HOT_MS = 5_000L
    }
}

/** Le magasin des modèles de l'application ; absent, chaque écran garde le sien. */
val LocalScreenModels = staticCompositionLocalOf<ScreenModels?> { null }

/** Le magasin à fournir à la racine : un seul par application. */
@Composable
fun rememberScreenModels(): ScreenModels {
    val scope = rememberCoroutineScope()
    return remember { ScreenModels(scope) }
}

/**
 * Le modèle d'un écran, pris dans [LocalScreenModels] sous [keys] ; sans magasin,
 * un modèle propre à l'écran, comme avant. [create] reçoit le scope à utiliser.
 */
@Composable
fun <T : Any> rememberScreenModel(vararg keys: Any?, create: (CoroutineScope) -> T): T {
    val store = LocalScreenModels.current
    val local = rememberCoroutineScope()
    return if (store != null) {
        remember(store, *keys) { store.getOrPut(keys.toList(), create) }
    } else {
        remember(*keys) { create(local) }
    }
}
